import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Reveal } from '../components/Reveal';
import { EnsoAnimated, BrushDivider, BonsaiMark } from '../components/Motifs';

const BONSAI = '/images/bonsai-editorial.png';

const About = () => {
    useEffect(() => {
        document.title = 'About — Nakasendo Capital';
    }, []);

    return (
        <div data-testid="about-page">
            {/* HERO */}
            <section className="relative px-6 md:px-12 lg:px-24 pt-12 md:pt-16 pb-20 lg:pb-32 overflow-hidden">
                <div
                    aria-hidden="true"
                    style={{ color: 'rgba(48, 58, 82, 0.05)' }}
                    className="absolute -right-10 top-10 font-jp text-[24vw] md:text-[14vw] leading-none pointer-events-none select-none whitespace-nowrap font-light"
                >
                    物語
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-end relative z-10">
                    <div className="lg:col-span-7">
                        <Reveal>
                            <p className="overline">— About Nakasendo Capital</p>
                        </Reveal>
                        <Reveal delay={0.15}>
                            <h1 className="mt-8 h1-editorial text-[clamp(2.5rem,7vw,7rem)] max-w-[16ch]">
                                Rooted in discipline, <br /><em>growing with purpose.</em>
                            </h1>
                        </Reveal>
                        <Reveal delay={0.35}>
                            <BrushDivider className="w-32 h-3 mt-10" color="#1A1A1A" />
                        </Reveal>
                    </div>
                    <div className="lg:col-span-5">
                        <Reveal delay={0.3} y={40}>
                            <div className="img-frame border border-ink/10">
                                <img
                                    src={BONSAI}
                                    alt="Bonsai tree in minimalist Japanese space"
                                    className="w-full aspect-[4/5] object-cover"
                                    loading="lazy"
                                />
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* OUR STORY */}
            <section className="px-6 md:px-12 lg:px-24 py-20 lg:py-32 border-t border-ink/10">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
                    <div className="lg:col-span-4">
                        <Reveal>
                            <p className="overline">— 01 / Our Story</p>
                        </Reveal>
                        <Reveal delay={0.15}>
                            <h2 className="mt-6 font-serif text-4xl md:text-5xl tracking-tight leading-[1.05]">
                                A journey best taken with a <em className="italic font-light">trusted companion.</em>
                            </h2>
                        </Reveal>
                    </div>
                    <div className="lg:col-span-7 lg:col-start-6 space-y-6 text-ink-soft text-base md:text-lg font-light leading-relaxed">
                        <Reveal delay={0.2}>
                            <p>
                                Nakasendo Capital was founded on a simple belief: growth is a journey
                                best taken with a trusted companion. The name <em className="font-serif text-ink">Nakasendō</em> (
                                <span className="font-jp text-ink not-italic">中山道</span>) comes from one of Japan's
                                historic roads — the inland route that connected Kyoto and Edo,
                                the two capitals of Japan.
                            </p>
                        </Reveal>
                        <Reveal delay={0.3}>
                            <p>
                                For centuries, it was the pathway where leaders, merchants, and travellers
                                exchanged ideas, traded goods, and built connections that shaped a nation.
                            </p>
                        </Reveal>
                        <Reveal delay={0.4}>
                            <p>
                                In that same spirit, we exist to guide modern enterprises on their own
                                journeys of value creation — helping them navigate markets, unlock
                                opportunities, and build lasting partnerships across regions and industries.
                            </p>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* MISSION */}
            <section className="relative px-6 md:px-12 lg:px-24 py-20 lg:py-32 border-t border-ink/10 overflow-hidden">
                <EnsoAnimated
                    className="absolute -left-10 top-10 w-72 h-72 text-aizome opacity-20 hidden md:block"
                    stroke="#303a52"
                    size={300}
                />
                <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
                    <div className="lg:col-span-4 lg:col-start-1">
                        <Reveal>
                            <p className="overline">— 02 / Our Mission</p>
                        </Reveal>
                    </div>
                    <div className="lg:col-span-7">
                        <Reveal delay={0.15}>
                            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.0]">
                                To help companies grow their value — <em className="italic font-light">with clarity, confidence, and connection.</em>
                            </h2>
                        </Reveal>
                        <Reveal delay={0.3}>
                            <p className="mt-10 text-ink-soft text-base md:text-lg font-light leading-relaxed max-w-2xl">
                                We partner with clients to map their journey and travel that journey with
                                them — articulating their equity story, structuring impactful transactions,
                                and forming strategic partnerships that drive sustainable growth. Our role
                                is to walk beside them, providing strategic insight and hands-on expertise.
                            </p>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* PHILOSOPHY: 3 PRINCIPLES */}
            <section className="px-6 md:px-12 lg:px-24 py-20 lg:py-32 border-t border-ink/10 bg-ivory">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
                    <div className="lg:col-span-5">
                        <Reveal>
                            <p className="overline">— 03 / Our Philosophy</p>
                        </Reveal>
                        <Reveal delay={0.15}>
                            <h2 className="mt-6 font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
                                Value creation as a <em className="italic font-light">continuum.</em>
                            </h2>
                        </Reveal>
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7 flex items-end">
                        <Reveal delay={0.3}>
                            <p className="text-ink-soft text-base md:text-lg leading-relaxed font-light max-w-lg">
                                A path built through insight, alignment, and disciplined execution.
                                Focus on the essentials that make the <em className="font-serif text-ink not-italic">PIE</em> bigger
                                — for clients, partners, and stakeholders alike.
                            </p>
                        </Reveal>
                    </div>
                </div>

                <div className="border-t border-ink/15">
                    {[
                        {
                            num: '01',
                            title: 'Partnership',
                            jp: '提携 — Teikei',
                            body: 'We walk alongside our clients, fostering trust and shared purpose. Every engagement is a relationship — not a transaction.',
                        },
                        {
                            num: '02',
                            title: 'Integrity',
                            jp: '誠実 — Seijitsu',
                            body: 'We deliver honest, independent advice grounded in ethics and expertise. Our counsel is unconflicted by mandate or volume.',
                        },
                        {
                            num: '03',
                            title: 'Execution',
                            jp: '実行 — Jikkō',
                            body: 'We bridge ideas and impact, accelerating adoption and implementation. Strategy without execution is just intention.',
                        },
                    ].map((p, i) => (
                        <Reveal key={p.num} delay={i * 0.1}>
                            <div className="group grid grid-cols-12 gap-6 py-10 md:py-14 border-b border-ink/15 transition-colors duration-500 hover:bg-aizome/[0.025]">
                                <div className="col-span-12 md:col-span-2">
                                    <span className="numeral text-6xl md:text-7xl">{p.num}</span>
                                </div>
                                <div className="col-span-12 md:col-span-4">
                                    <h3 className="font-serif text-3xl md:text-4xl tracking-tight">
                                        {p.title}
                                    </h3>
                                    <p className="font-jp text-sm text-ink-soft mt-2">{p.jp}</p>
                                </div>
                                <div className="col-span-12 md:col-span-6">
                                    <p className="text-ink-soft text-base md:text-lg font-light leading-relaxed">
                                        {p.body}
                                    </p>
                                </div>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* WHO WE WORK WITH */}
            <section className="px-6 md:px-12 lg:px-24 py-20 lg:py-32 border-t border-ink/10">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
                    <div className="lg:col-span-5">
                        <Reveal>
                            <p className="overline">— 04 / Who We Work With</p>
                        </Reveal>
                        <Reveal delay={0.15}>
                            <h2 className="mt-6 font-serif text-4xl md:text-5xl tracking-tight leading-[1.05]">
                                Sectors shaping the <em className="italic font-light">next industrial era.</em>
                            </h2>
                        </Reveal>
                        <Reveal delay={0.3}>
                            <BonsaiMark className="mt-10 text-aizome" stroke="#303a52" size={64} />
                        </Reveal>
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7 space-y-6 text-ink-soft text-base md:text-lg font-light leading-relaxed">
                        <Reveal delay={0.2}>
                            <p>
                                We work with companies and investors in sectors shaping the next
                                industrial era — <em className="font-serif text-ink not-italic">energy, infrastructure, and clean innovation.</em>
                            </p>
                        </Reveal>
                        <Reveal delay={0.3}>
                            <p>
                                Our clients include growth-stage businesses preparing for expansion or
                                fundraising, as well as established enterprises seeking to reposition,
                                divest, or form responsible partnerships across Asia and beyond.
                            </p>
                        </Reveal>
                        <Reveal delay={0.4}>
                            <ul className="pt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 border-t border-ink/15 mt-8">
                                {['Energy Transition', 'Infrastructure', 'Industrial Tech', 'New Technology'].map((s) => (
                                    <li key={s} className="flex items-center gap-3 font-serif text-xl text-ink">
                                        <span className="w-2 h-px bg-ink" />
                                        {s}
                                    </li>
                                ))}
                            </ul>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* OUR EDGE */}
            <section className="px-6 md:px-12 lg:px-24 py-20 lg:py-32 border-t border-ink/10 bg-aizome text-ivory relative overflow-hidden">
                <div
                    aria-hidden="true"
                    style={{ color: 'rgba(253, 251, 247, 0.06)' }}
                    className="absolute -left-6 -bottom-12 font-jp text-[20vw] md:text-[12vw] leading-none pointer-events-none select-none whitespace-nowrap font-light"
                >
                    優位
                </div>
                <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
                    <div className="lg:col-span-4">
                        <Reveal>
                            <p className="overline text-ivory/60">— 05 / Our Edge</p>
                        </Reveal>
                        <Reveal delay={0.15}>
                            <h2 className="mt-6 font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.0]">
                                Senior attention, <em className="italic font-light">independent thought.</em>
                            </h2>
                        </Reveal>
                    </div>
                    <div className="lg:col-span-7 lg:col-start-6 grid grid-cols-1 md:grid-cols-2 gap-px bg-ivory/15">
                        {[
                            {
                                t: 'Cross-Market Experience',
                                d: 'Deep expertise in energy transition, infrastructure, and industrial technologies.',
                            },
                            {
                                t: 'Asia Bridge',
                                d: 'Strong networks and cultural fluency to connect global pools of capital and Asian opportunities.',
                            },
                            {
                                t: 'Execution Mindset',
                                d: 'We translate ideas into measurable outcomes, driving delivery in complex environments.',
                            },
                            {
                                t: 'Ethical Foundation',
                                d: 'Committed to transparency, fairness, and value creation that endures.',
                            },
                            {
                                t: 'Boutique Focus',
                                d: 'Senior attention, tailored engagement, and independence of thought.',
                            },
                            {
                                t: 'Asian Reach',
                                d: 'Built on relationships across Singapore, Tokyo, and beyond.',
                            },
                        ].map((e, i) => (
                            <Reveal key={e.t} delay={i * 0.07} className="bg-aizome">
                                <div className="p-8 md:p-10 h-full">
                                    <h3 className="font-serif text-2xl md:text-3xl tracking-tight">{e.t}</h3>
                                    <p className="mt-4 text-ivory/70 font-light text-[15px] leading-relaxed max-w-[34ch]">
                                        {e.d}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* JOURNEY AHEAD CTA */}
            <section className="px-6 md:px-12 lg:px-24 py-24 lg:py-40 border-t border-ink/10 text-center">
                <Reveal>
                    <p className="overline">— The Journey Ahead</p>
                </Reveal>
                <Reveal delay={0.15}>
                    <h2 className="mt-6 font-serif text-4xl md:text-6xl lg:text-7xl tracking-tight leading-[1.0] max-w-4xl mx-auto">
                        Every engagement is a <em className="italic font-light">shared journey.</em>
                    </h2>
                </Reveal>
                <Reveal delay={0.3}>
                    <p className="mt-8 text-ink-soft text-base md:text-lg font-light leading-relaxed max-w-2xl mx-auto">
                        One that demands both vision and endurance. Let's start your journey.
                    </p>
                </Reveal>
                <Reveal delay={0.45}>
                    <Link to="/contact" data-testid="about-cta" className="btn-primary mt-12">
                        Get in Touch <span className="arrow">→</span>
                    </Link>
                </Reveal>
            </section>
        </div>
    );
};

export default About;
